import type {
	API_AssignStaffToEstablishmentRequest,
	API_AssignStaffToEstablishmentResponse,
	API_BulkAssignStaffToEstablishmentsRequest,
	API_BulkAssignStaffToEstablishmentsResponse,
	API_ListEstablishmentStaffResponse,
	API_ListStaffEstablishmentAssignmentsResponse,
	API_RemoveStaffFromEstablishmentResponse,
} from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// API functions
export const fetchEstablishmentStaff = (
	token: string,
	organizationId: string,
	establishmentId: string,
): Promise<API_ListEstablishmentStaffResponse> =>
	api
		.get(
			`/organizations/${organizationId}/establishments/${establishmentId}/staff`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const assignStaffToEstablishment = (
	token: string,
	organizationId: string,
	establishmentId: string,
	data: API_AssignStaffToEstablishmentRequest,
): Promise<API_AssignStaffToEstablishmentResponse> =>
	api
		.post(
			`/organizations/${organizationId}/establishments/${establishmentId}/staff`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const bulkAssignStaffToEstablishments = (
	token: string,
	organizationId: string,
	data: API_BulkAssignStaffToEstablishmentsRequest,
): Promise<API_BulkAssignStaffToEstablishmentsResponse> =>
	api
		.post(
			`/organizations/${organizationId}/staff/bulk-assign-establishments`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const fetchStaffEstablishmentAssignments = (
	token: string,
	organizationId: string,
	staffUserId: string,
): Promise<API_ListStaffEstablishmentAssignmentsResponse> =>
	api
		.get(
			`/organizations/${organizationId}/staff/${staffUserId}/establishments`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const removeStaffFromEstablishment = (
	token: string,
	organizationId: string,
	establishmentId: string,
	staffUserId: string,
): Promise<API_RemoveStaffFromEstablishmentResponse> =>
	api
		.delete(
			`/organizations/${organizationId}/establishments/${establishmentId}/staff/${staffUserId}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

// Query options
export const fetchEstablishmentStaffQueryOptions = (
	token: string,
	organizationId: string,
	establishmentId: string,
) =>
	queryOptions({
		queryKey: ["establishment_staff", organizationId, establishmentId],
		queryFn: () =>
			fetchEstablishmentStaff(token, organizationId, establishmentId),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});

export const fetchStaffEstablishmentAssignmentsQueryOptions = (
	token: string,
	organizationId: string,
	staffUserId: string,
) =>
	queryOptions({
		queryKey: ["staff_establishment_assignments", organizationId, staffUserId],
		queryFn: () =>
			fetchStaffEstablishmentAssignments(token, organizationId, staffUserId),
		staleTime: 2 * 60 * 1000, // 2 minutes
	});

// Mutation hooks
export const useAssignStaffToEstablishment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			establishmentId,
			data,
		}: {
			token: string;
			organizationId: string;
			establishmentId: string;
			data: API_AssignStaffToEstablishmentRequest;
		}) =>
			assignStaffToEstablishment(token, organizationId, establishmentId, data),
		toastMessages: {
			loading: t("establishmentStaff.toasts.assigning"),
			success: t("establishmentStaff.toasts.assignSuccess"),
			error: t("establishmentStaff.toasts.assignError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch establishment staff
			queryClient.invalidateQueries({
				queryKey: [
					"establishment_staff",
					variables.organizationId,
					variables.establishmentId,
				],
			});

			// Also invalidate organization staff since establishment assignments might affect permissions
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});

			// Invalidate user permissions since roles might have changed
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};

export const useBulkAssignStaffToEstablishments = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_BulkAssignStaffToEstablishmentsRequest;
		}) => bulkAssignStaffToEstablishments(token, organizationId, data),
		toastMessages: {
			loading: t("establishmentStaff.toasts.bulkAssigning"),
			success: t("establishmentStaff.toasts.bulkAssignSuccess"),
			error: t("establishmentStaff.toasts.bulkAssignError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate establishment staff for all affected establishments
			for (const establishmentId of variables.data.establishmentIds) {
				queryClient.invalidateQueries({
					queryKey: [
						"establishment_staff",
						variables.organizationId,
						establishmentId,
					],
				});
			}

			// Invalidate staff establishment assignments
			queryClient.invalidateQueries({
				queryKey: [
					"staff_establishment_assignments",
					variables.organizationId,
					variables.data.staffUserId,
				],
			});

			// Also invalidate organization staff since establishment assignments might affect permissions
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});

			// Invalidate user permissions since roles might have changed
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};

export const useRemoveStaffFromEstablishment = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			establishmentId,
			staffUserId,
		}: {
			token: string;
			organizationId: string;
			establishmentId: string;
			staffUserId: string;
		}) =>
			removeStaffFromEstablishment(
				token,
				organizationId,
				establishmentId,
				staffUserId,
			),
		toastMessages: {
			loading: t("establishmentStaff.toasts.removing"),
			success: t("establishmentStaff.toasts.removeSuccess"),
			error: t("establishmentStaff.toasts.removeError"),
		},
		onSuccess: (_, variables) => {
			// Invalidate and refetch establishment staff
			queryClient.invalidateQueries({
				queryKey: [
					"establishment_staff",
					variables.organizationId,
					variables.establishmentId,
				],
			});

			// Invalidate staff establishment assignments
			queryClient.invalidateQueries({
				queryKey: [
					"staff_establishment_assignments",
					variables.organizationId,
					variables.staffUserId,
				],
			});

			// Also invalidate organization staff since establishment assignments might affect permissions
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});

			// Also invalidate user permissions since staff was removed
			queryClient.invalidateQueries({
				queryKey: ["user_permissions"],
			});
		},
	});
};
